import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { OccupationClaim } from '../models/occupation.model';
import { formatHourLabel } from '../occupation-scales';

/**
 * What one slot of the ladder is holding. A block is a claim, not a swept step, so it
 * reports a single route's window rather than the total across routes the area tooltip
 * gives — the two forms answer different questions and cannot share a template.
 */
@Component({
  selector: 'app-occupation-block-tooltip',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
  template: `
    <div
      data-testid="occupation-block-tooltip"
      class="bg-elevated border-strong rounded-md border p-2 text-xs shadow-lg">
      <div class="border-weak mb-1 flex items-baseline justify-between gap-3 border-b pb-1">
        <span class="text-text-primary font-mono text-[11px] font-semibold">
          {{ claim().routeUid }}
        </span>
        <span class="text-text-secondary font-mono text-[11px] tabular-nums">
          {{ range() }}
        </span>
      </div>
      <p class="text-text-secondary font-mono text-[11px]">
        {{ claim().stopoverUid }}
      </p>
      <p class="text-text-secondary text-[11px]">
        {{
          'visualization.occupation.tooltip.slot'
            | translate: { slot: laneIndex() + 1, quantity: claim().quantity }
        }}
      </p>
      @if (overflow()) {
        <p class="text-danger mt-1 text-[11px] font-semibold">
          {{ 'visualization.occupation.tooltip.overflow' | translate }}
        </p>
      }
    </div>
  `,
  // See `OccupationTooltipComponent`: a hit-testable host over the mark it describes
  // takes the pointer off it and the two flicker against each other.
  styles: `
    :host {
      pointer-events: none;
    }
  `
})
export class OccupationBlockTooltipComponent {
  readonly claim = input.required<OccupationClaim>();
  /** 0-based lane, shown one-based: slots are counted from one by everyone who uses them. */
  readonly laneIndex = input.required<number>();
  readonly overflow = input.required<boolean>();

  protected readonly range = computed(
    () => `${formatHourLabel(this.claim().startMs)}–${formatHourLabel(this.claim().endMs)}`
  );
}
